// Cruise Command · Editorial Image Library
//
// Governed by LAW-PHOTO-001 (see
// /app/docs/governance/editorial/LAW-PHOTO-001-EDITORIAL-PHOTOGRAPHY-STANDARD.md).
//
// SHIP RULE (absolute): no cruise ships. Small vessels (rowboats,
// sailboats, dinghies, fishing skiffs) are permitted only when they
// are incidental to the atmosphere — the vessel is not the subject.
//
// EDITORIAL OWNERSHIP: Engineering owns this file's mechanism.
// Editorial owns the entries. Every addition, removal, or replacement
// of an entry requires PO ratification recorded in `ratified_by`.
//
// ENTRY SHAPE (LAW-PHOTO-001 §Editorial Image Library):
//   {
//     id, url, alt, attribution, register,
//     focal_point:  { x, y },   // 0..100 percentages
//     crop_hint:    "…",        // curator note on quiet quadrant
//     added_at, ratified_by,
//   }
//
// REGISTERS:
//   cruise_ship_port | cruise_ship_sea | onboard_deck_horizon |
//   caribbean_editorial | nordic_fjord | mediterranean | alpine |
//   harbour | coastal | city_quiet | wilderness
//
// Curation status:  5 / 5 active · PO 2026-02-15 · RC7 managed-
// editorial-library ingest. Five entries: the original signature port
// image plus four PO-supplied managed assets delivered through the
// established reference-media workflow. Library considered complete
// per PO acceptance criterion — no further discovery or expansion.

const SESSION_KEY = "cc-editorial-image-id";

export const EDITORIAL_IMAGES = [
  {
    id:           "img-cruise-port-five-ships",
    url:          "https://images.unsplash.com/photo-1548574505-5e239809ee19?auto=format&fit=crop&w=2000&q=80",
    alt:          "An aerial cruise port at midday — five cruise ships docked side-by-side on turquoise water beside a coastal town.",
    attribution:  "Unsplash editorial · Cruise port (aerial)",
    register:     "cruise_ship_port",
    focal_point:  { x: 60, y: 55 },
    crop_hint:    "Largest ship anchors right-of-centre; coastal town crops naturally at the seam on the verso side.",
    added_at:     "2026-02-14",
    ratified_by:  "PO · RC7 signature-image activation · visually inspected via analyze_file_tool (95% confidence) · 2026-02-14",
  },
  {
    id:           "img-onboard-deck-horizon",
    url:          "https://customer-assets-m6fa6gv7.emergentagent.net/job_1edd156c-5738-4150-a480-602ec4a7bb5c/artifacts/ktgg4b3s_image4.jfif",
    alt:          "A premium cruise-ship promenade at midday — teak deck, brass rail, empty loungers looking out onto the open blue ocean beneath a cloudless sky, two champagne flutes on a side table.",
    attribution:  "Cruise Command · managed editorial asset (PO ingest)",
    register:     "onboard_deck_horizon",
    focal_point:  { x: 40, y: 60 },
    crop_hint:    "Loungers anchor lower-left; the upper-right sky is quiet negative space for typography on the verso.",
    added_at:     "2026-02-15",
    ratified_by:  "PO · RC7 managed-image-library ingest · pixel-verified via analyze_file_tool · 2026-02-15",
  },
  {
    id:           "img-cruise-ship-sunset",
    url:          "https://customer-assets-m6fa6gv7.emergentagent.net/job_1edd156c-5738-4150-a480-602ec4a7bb5c/artifacts/uyk1hnyi_image3.webp",
    alt:          "A modern luxury cruise ship underway on calm water at sunset — pastel pink, orange and lavender sky, faint coastline on the horizon, warm interior lights along the vessel.",
    attribution:  "Cruise Command · managed editorial asset (PO ingest)",
    register:     "cruise_ship_sea",
    focal_point:  { x: 55, y: 55 },
    crop_hint:    "Ship centres slightly right; the entire upper band is warm pastel sky — ideal for headline typography on the verso.",
    added_at:     "2026-02-15",
    ratified_by:  "PO · RC7 managed-image-library ingest · pixel-verified via analyze_file_tool · 2026-02-15",
  },
  {
    id:           "img-caribbean-cove-cruise",
    url:          "https://customer-assets-m6fa6gv7.emergentagent.net/job_1edd156c-5738-4150-a480-602ec4a7bb5c/artifacts/71546ge8_image2.jfif",
    alt:          "A Caribbean cove framed by green hills — turquoise shallows fading to deep blue water, a small crescent of white sand, red-roofed buildings on a hillside, and a cruise ship anchored offshore beneath scattered clouds.",
    attribution:  "Cruise Command · managed editorial asset (PO ingest)",
    register:     "caribbean_editorial",
    focal_point:  { x: 45, y: 55 },
    crop_hint:    "Cove and cruise ship read together in the centre band; upper sky provides typography space on the verso.",
    added_at:     "2026-02-15",
    ratified_by:  "PO · RC7 managed-image-library ingest · pixel-verified via analyze_file_tool · 2026-02-15",
  },
  {
    id:           "img-aerial-cruise-ship-underway",
    url:          "https://customer-assets-m6fa6gv7.emergentagent.net/job_1edd156c-5738-4150-a480-602ec4a7bb5c/artifacts/6hj94ois_image1.jfif",
    alt:          "An aerial three-quarter view of a large modern cruise ship with a deep-blue hull underway on open ocean — long wake trailing to the stern, deep-blue water surrounding the vessel.",
    attribution:  "Cruise Command · managed editorial asset (PO ingest)",
    register:     "cruise_ship_sea",
    focal_point:  { x: 50, y: 55 },
    crop_hint:    "Ship anchors the centre; the surrounding blue ocean provides negative space on every side of the frame.",
    added_at:     "2026-02-15",
    ratified_by:  "PO · RC7 managed-image-library ingest · pixel-verified via analyze_file_tool · 2026-02-15",
  },
];

// ── Retired entries (LAW-PHOTO-001 provenance) ─────────────────
// Kept as historical record only — NOT part of the active
// rotation. Any component that reads `EDITORIAL_IMAGES` will never
// select a retired entry.
//
//   {
//     id:          "img-alpine-braies-rowboat",
//     url:         "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&w=2000&q=80",
//     alt:         "A quiet stretch of water at late afternoon — the atmosphere of the voyage.",
//     attribution: "Unsplash editorial · Pragser Wildsee, Italian Dolomites",
//     register:    "alpine",
//     focal_point: { x: 62, y: 55 },
//     crop_hint:   "Upper-left quadrant is quiet — reserves space for type on the verso.",
//     added_at:    "2026-08-09",
//     ratified_by: "PO · RC6-L01 opening-spread selection · 2026-08-09",
//     retired_at:  "2026-02-14",
//     retired_by:  "PO · RC7 image-library refresh · did not communicate cruise-platform identity",
//   },


/**
 * Return one entry from the editorial library, sticky for the current
 * browser session.  A visitor experiences one issue per session — a
 * refresh, an auth transition, or a route change does not re-roll the
 * dice.  A future visit (after the tab closes) may bring a different
 * photograph.  Private-mode / storage-blocked browsers fall back to
 * the first entry so the spread never renders imageless.
 *
 * LAW-PHOTO-001 · "The visitor should feel they are opening one issue,
 * not spinning a slot machine."
 */
export function getSessionEditorialImage() {
  // Empty library is now a supported state (RC7 · PO 2026-02-14):
  // the front door falls back to the pure-navy recto with the
  // editorial verso + SSO buttons — verified acceptable during the
  // fallback audit. Returns null; consumers must treat that as
  // "no photograph this session".
  if (!EDITORIAL_IMAGES.length) return null;
  // Node/SSR safety — no window during test rendering.
  if (typeof window === "undefined") return EDITORIAL_IMAGES[0];
  try {
    const stored = window.sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      const found = EDITORIAL_IMAGES.find((e) => e.id === stored);
      if (found) return found;
    }
    const chosen = EDITORIAL_IMAGES[Math.floor(Math.random() * EDITORIAL_IMAGES.length)];
    window.sessionStorage.setItem(SESSION_KEY, chosen.id);
    return chosen;
  } catch (_e) {
    // sessionStorage disabled (privacy mode, third-party frame, etc.)
    return EDITORIAL_IMAGES[0];
  }
}


/**
 * Compose a CSS `background-position` value from an entry's focal
 * point.  Ensures the subject of the photograph survives every
 * viewport width and the negative-space quadrant intended for
 * typography remains where the curator placed it.
 */
export function backgroundPositionFor(entry) {
  const fp = (entry && entry.focal_point) || { x: 50, y: 50 };
  return `${fp.x}% ${fp.y}%`;
}


// Legacy export retained so any surface still importing the previous
// per-render random picker does not break.  New surfaces should call
// `getSessionEditorialImage` instead.
export function pickEditorialImage() {
  return getSessionEditorialImage();
}

export default EDITORIAL_IMAGES;
